import React, { useEffect, useState, useRef } from "react";
import type { NotificationTemplate, NotificationAudience } from "@types";
import {
  fetchNotificationTemplates,
  createNotificationTemplate,
  updateNotificationTemplate,
  deleteNotificationTemplate,
} from "../../lib/notificationsService";

type FormData = {
  key: string;
  send_via_email: boolean;
  send_via_text: boolean;
  subject: string;
  body: string;
  sms_body: string;
  description: string;
  variables: string;
};

type ActiveField = "subject" | "body" | "sms_body" | null;

export default function NotificationTemplatesPage() {
  const [audience, setAudience] = useState<NotificationAudience>("seeker");
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [formData, setFormData] = useState<FormData>({
    key: "",
    send_via_email: true,
    send_via_text: false,
    subject: "",
    body: "",
    sms_body: "",
    description: "",
    variables: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeField, setActiveField] = useState<ActiveField>(null);
  const [modifyVariables, setModifyVariables] = useState(false);
  const [modifyKeyAndDescription, setModifyKeyAndDescription] = useState(false);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const smsBodyTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadTemplates();
  }, [audience]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  async function loadTemplates() {
    try {
      setLoading(true);
      const data = await fetchNotificationTemplates(audience);
      setTemplates(data);
    } catch (err) {
      console.error("Failed to load notification templates:", err);
      setError("Failed to load notification templates");
    } finally {
      setLoading(false);
    }
  }

  function openCreateModal() {
    setEditingTemplate(null);
    setModifyVariables(false);
    setModifyKeyAndDescription(false);
    setFormData({
      key: "",
      send_via_email: true,
      send_via_text: false,
      subject: "",
      body: "",
      sms_body: "",
      description: "",
      variables: "",
    });
    setShowModal(true);
    setError(null);
  }

  function openEditModal(template: NotificationTemplate) {
    setEditingTemplate(template);
    setModifyVariables(false);
    setModifyKeyAndDescription(false);
    setFormData({
      key: template.key,
      send_via_email: template.send_via_email,
      send_via_text: template.send_via_text,
      subject: template.subject || "",
      body: template.body,
      sms_body: template.sms_body || "",
      description: template.description || "",
      variables: template.variables ? template.variables.join(", ") : "",
    });
    setShowModal(true);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      if (editingTemplate) {
        // Update existing template
        const updateData: any = {
          send_via_email: formData.send_via_email,
          send_via_text: formData.send_via_text,
          subject: formData.subject || null,
          body: formData.body,
          sms_body: formData.sms_body || null,
        };

        // If modifying key and description, include them
        if (modifyKeyAndDescription) {
          updateData.key = formData.key;
          updateData.description = formData.description || null;
        }

        // If modifying variables, parse and include them
        if (modifyVariables) {
          const variablesArray = formData.variables
            .split(",")
            .map(v => v.trim())
            .filter(v => v.length > 0);
          updateData.variables = variablesArray.length > 0 ? variablesArray : null;
        }

        await updateNotificationTemplate(editingTemplate.id, updateData);
        setSuccess("Template updated successfully");
      } else {
        // Create new template
        // Parse variables from comma-separated string
        const variablesArray = formData.variables
          .split(",")
          .map(v => v.trim())
          .filter(v => v.length > 0);

        await createNotificationTemplate({
          key: formData.key,
          send_via_email: formData.send_via_email,
          send_via_text: formData.send_via_text,
          audience,
          subject: formData.subject || null,
          body: formData.body,
          sms_body: formData.sms_body || null,
          description: formData.description || null,
          variables: variablesArray.length > 0 ? variablesArray : null,
          updated_by: null, // TODO: Set to current admin user ID
        });
        setSuccess("Template created successfully");
      }

      setShowModal(false);
      loadTemplates();
    } catch (err: any) {
      console.error("Error saving template:", err);
      setError(err.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  }

  function insertVariable(variable: string) {
    const formattedVariable = `{{${variable}}}`;

    if (activeField === "subject" && subjectInputRef.current) {
      const input = subjectInputRef.current;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const newValue = formData.subject.slice(0, start) + formattedVariable + formData.subject.slice(end);
      setFormData({ ...formData, subject: newValue });

      // Set cursor position after inserted variable
      setTimeout(() => {
        input.focus();
        const newPosition = start + formattedVariable.length;
        input.setSelectionRange(newPosition, newPosition);
      }, 0);
    } else if (activeField === "body" && bodyTextareaRef.current) {
      const textarea = bodyTextareaRef.current;
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const newValue = formData.body.slice(0, start) + formattedVariable + formData.body.slice(end);
      setFormData({ ...formData, body: newValue });

      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus();
        const newPosition = start + formattedVariable.length;
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
    } else if (activeField === "sms_body" && smsBodyTextareaRef.current) {
      const textarea = smsBodyTextareaRef.current;
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const newValue = formData.sms_body.slice(0, start) + formattedVariable + formData.sms_body.slice(end);
      setFormData({ ...formData, sms_body: newValue });

      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus();
        const newPosition = start + formattedVariable.length;
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  }

  async function handleDelete(template: NotificationTemplate) {
    if (!confirm(`Are you sure you want to delete the template "${template.key}"?`)) {
      return;
    }

    try {
      await deleteNotificationTemplate(template.id);
      setSuccess("Template deleted successfully");
      loadTemplates();
    } catch (err) {
      console.error("Error deleting template:", err);
      setError("Failed to delete template");
    }
  }

  return (
    <div className="min-h-screen bg-[#FFFCEF] px-4 py-8 text-[#2C2731]">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-recoleta text-3xl font-bold text-[#2C2731]">
            Notification Templates
          </h1>
          <p className="mt-2 text-sm text-[#2C2731]/70">
            Manage email and text notification templates for each audience
          </p>
        </div>

        {/* Success/Error Alerts */}
        {success && (
          <div className="mb-6 flex items-start justify-between gap-4 rounded-2xl border border-[#214D3C]/40 bg-[#E4F1E8] px-4 py-3 text-sm text-[#214D3C]">
            <p>{success}</p>
            <button
              onClick={() => setSuccess(null)}
              className="text-[#214D3C]/60 hover:text-[#214D3C]"
            >
              Dismiss
            </button>
          </div>
        )}

        {error && (
          <div className="mb-6 flex items-start justify-between gap-4 rounded-2xl border border-[#B24E4E]/40 bg-[#FCEDEA] px-4 py-3 text-sm text-[#B24E4E]">
            <p>{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-[#B24E4E]/60 hover:text-[#B24E4E]"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 flex gap-2 border-b border-[#2C2731]/20">
          <button
            onClick={() => setAudience("seeker")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              audience === "seeker"
                ? "border-b-2 border-[#2C2731] text-[#2C2731]"
                : "text-[#2C2731]/60 hover:text-[#2C2731]"
            }`}
          >
            Seeker Templates
          </button>
          <button
            onClick={() => setAudience("guide")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              audience === "guide"
                ? "border-b-2 border-[#2C2731] text-[#2C2731]"
                : "text-[#2C2731]/60 hover:text-[#2C2731]"
            }`}
          >
            Guide Templates
          </button>
          <button
            onClick={() => setAudience("admin")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              audience === "admin"
                ? "border-b-2 border-[#2C2731] text-[#2C2731]"
                : "text-[#2C2731]/60 hover:text-[#2C2731]"
            }`}
          >
            Admin Templates
          </button>
          <button
            onClick={() => setAudience("shared")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              audience === "shared"
                ? "border-b-2 border-[#2C2731] text-[#2C2731]"
                : "text-[#2C2731]/60 hover:text-[#2C2731]"
            }`}
          >
            Shared Templates
          </button>
        </div>

        {/* Create Button */}
        <div className="mb-6">
          <button
            onClick={openCreateModal}
            className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2.5 text-sm font-medium text-[#FEF6D6] shadow-md hover:bg-[#3C3450]"
          >
            + Create Template
          </button>
        </div>

        {/* Templates List */}
        {loading ? (
          <p className="text-center text-sm text-[#2C2731]/60">Loading templates...</p>
        ) : templates.length === 0 ? (
          <div className="rounded-2xl border border-[#2C2731]/20 bg-white p-8 text-center">
            <p className="text-sm text-[#2C2731]/60">
              No templates found for {audience} audience. Create your first template above.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="rounded-2xl border border-[#2C2731]/20 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium text-[#2C2731]">{template.key}</h3>
                      <div className="flex gap-2">
                        {!template.send_via_email && !template.send_via_text ? (
                          <span className="rounded-full bg-[#B24E4E]/10 px-2 py-0.5 text-xs font-medium text-[#B24E4E]">
                            Disabled
                          </span>
                        ) : (
                          <>
                            {template.send_via_email && (
                              <span className="rounded-full bg-[#214D3C]/10 px-2 py-0.5 text-xs text-[#214D3C]">
                                Email
                              </span>
                            )}
                            {template.send_via_text && (
                              <span className="rounded-full bg-[#403851]/10 px-2 py-0.5 text-xs text-[#403851]">
                                Text
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {template.description && (
                      <p className="text-sm text-[#2C2731]/70 mb-3">{template.description}</p>
                    )}
                    {template.subject && (
                      <p className="text-sm mb-2">
                        <span className="font-medium text-[#2C2731]/80">Subject:</span>{" "}
                        <span className="text-[#2C2731]/70">{template.subject}</span>
                      </p>
                    )}
                    <p className="text-sm mb-2">
                      <span className="font-medium text-[#2C2731]/80">Email Body:</span>{" "}
                      <span className="text-[#2C2731]/70 line-clamp-2">{template.body}</span>
                    </p>
                    {template.sms_body && (
                      <p className="text-sm mb-3">
                        <span className="font-medium text-[#2C2731]/80">SMS Body:</span>{" "}
                        <span className="text-[#2C2731]/70 line-clamp-2">{template.sms_body}</span>
                      </p>
                    )}
                    {template.variables && template.variables.length > 0 && (
                      <p className="text-xs text-[#2C2731]/60">
                        <span className="font-medium">Variables:</span> {template.variables.join(", ")}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(template)}
                      className="rounded-lg border border-[#2C2731] px-3 py-1.5 text-sm font-medium text-[#2C2731] hover:bg-[#F2EAC3]"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(template)}
                      className="rounded-lg border border-[#B24E4E] px-3 py-1.5 text-sm font-medium text-[#B24E4E] hover:bg-[#FCEDEA]"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[#2C2731]/70 px-4 py-16">
            <div className="w-full max-w-6xl rounded-2xl border border-[#2C2731] bg-[#FFFCEF] text-[#2C2731] shadow-xl">
              {/* Header with Key and Description */}
              <div className="border-b border-[#2C2731]/20 px-6 py-5">
                <h2 className="font-recoleta text-xl font-bold mb-1">
                  {editingTemplate ? "Edit Template" : "Create Template"}
                </h2>
                {editingTemplate && (
                  <>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="rounded-lg bg-[#2C2731] px-3 py-1.5">
                        <p className="font-mono text-sm font-medium text-[#FEF6D6]">
                          {editingTemplate.key}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {editingTemplate.send_via_email && (
                          <span className="rounded-full bg-[#214D3C]/10 px-2 py-0.5 text-xs text-[#214D3C]">
                            Email
                          </span>
                        )}
                        {editingTemplate.send_via_text && (
                          <span className="rounded-full bg-[#403851]/10 px-2 py-0.5 text-xs text-[#403851]">
                            Text
                          </span>
                        )}
                      </div>
                    </div>
                    {editingTemplate.description && (
                      <p className="mt-2 text-sm text-[#2C2731]/70">
                        {editingTemplate.description}
                      </p>
                    )}
                  </>
                )}
                {!editingTemplate && (
                  <p className="mt-1 text-sm text-[#2C2731]/60">
                    Create a new notification template for {audience} users
                  </p>
                )}
              </div>

              <div className="flex">
                {/* Main Form */}
                <form onSubmit={handleSubmit} className="flex-1 p-6">
                  {error && (
                    <div className="mb-4 rounded-lg border border-[#B24E4E]/40 bg-[#FCEDEA] px-4 py-3 text-sm text-[#B24E4E]">
                      {error}
                    </div>
                  )}

                  {/* Warning for Create Mode */}
                  {!editingTemplate && (
                    <div className="mb-4 rounded-lg border border-[#B24E4E]/40 bg-[#FCEDEA] px-4 py-3 text-sm text-[#B24E4E]">
                      <p className="font-medium mb-1">⚠️ Backend Implementation Required</p>
                      <p>
                        Keys undefined in the backend will not be sent. Please contact the maintainer to implement any additional templates you require.
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                  {/* Key - Only for Create */}
                  {!editingTemplate && (
                    <>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-[#2C2731]/80">
                          Template Key *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.key}
                          onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                          placeholder="e.g., welcome_email, session_reminder"
                          className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                        />
                        <p className="mt-1 text-xs text-[#2C2731]/60">
                          Unique identifier for code-based lookup
                        </p>
                      </div>

                      {/* Description - Only for Create */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-[#2C2731]/80">
                          Description
                        </label>
                        <input
                          type="text"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="Brief description of when this template is used"
                          className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                        />
                      </div>

                      {/* Variables - Only for Create */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-[#2C2731]/80">
                          Template Variables
                        </label>
                        <input
                          type="text"
                          value={formData.variables}
                          onChange={(e) => setFormData({ ...formData, variables: e.target.value })}
                          placeholder="e.g., user.name, session.date, guide.name"
                          className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                        />
                        <p className="mt-1 text-xs text-[#2C2731]/60">
                          Comma-separated list of allowed variables
                        </p>
                      </div>
                    </>
                  )}

                  {/* Send Via */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-[#2C2731]/80">
                      Send Via *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.send_via_email}
                          onChange={(e) =>
                            setFormData({ ...formData, send_via_email: e.target.checked })
                          }
                          className="h-4 w-4 rounded border-[#2C2731] text-[#2C2731] focus:ring-2 focus:ring-[#2C2731]/30"
                        />
                        <span className="text-sm text-[#2C2731]">Email</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.send_via_text}
                          onChange={(e) =>
                            setFormData({ ...formData, send_via_text: e.target.checked })
                          }
                          className="h-4 w-4 rounded border-[#2C2731] text-[#2C2731] focus:ring-2 focus:ring-[#2C2731]/30"
                        />
                        <span className="text-sm text-[#2C2731]">Text</span>
                      </label>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-[#2C2731]/80">
                      Email Subject {formData.send_via_email && "*"}
                      {!formData.send_via_email && (
                        <span className="ml-2 text-xs font-normal text-[#2C2731]/50 italic">
                          (will not be used)
                        </span>
                      )}
                    </label>
                    <input
                      ref={subjectInputRef}
                      type="text"
                      required={formData.send_via_email}
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      onFocus={() => setActiveField("subject")}
                      placeholder="Email subject line"
                      disabled={!formData.send_via_email}
                      className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                        formData.send_via_email
                          ? "border-[#2C2731] bg-[#FFFCEF] text-[#2C2731] focus:border-[#2C2731] focus:ring-[#2C2731]/30"
                          : "border-[#2C2731]/30 bg-[#2C2731]/5 text-[#2C2731]/50 cursor-not-allowed"
                      }`}
                    />
                  </div>

                  {/* Body */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-[#2C2731]/80">
                      Email Body *
                    </label>
                    <textarea
                      ref={bodyTextareaRef}
                      required
                      value={formData.body}
                      onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                      onFocus={() => setActiveField("body")}
                      placeholder="Email message content. Use {{variable}} syntax for dynamic values."
                      rows={8}
                      className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                    />
                  </div>

                  {/* SMS Body */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-[#2C2731]/80">
                      SMS Body
                      {!formData.send_via_text && (
                        <span className="ml-2 text-xs font-normal text-[#2C2731]/50 italic">
                          (will not be used)
                        </span>
                      )}
                    </label>
                    <textarea
                      ref={smsBodyTextareaRef}
                      value={formData.sms_body}
                      onChange={(e) => setFormData({ ...formData, sms_body: e.target.value })}
                      onFocus={() => setActiveField("sms_body")}
                      placeholder="SMS message content. Keep it concise. If empty, email body will be used."
                      rows={4}
                      disabled={!formData.send_via_text}
                      className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                        formData.send_via_text
                          ? "border-[#2C2731] bg-[#FFFCEF] text-[#2C2731] focus:border-[#2C2731] focus:ring-[#2C2731]/30"
                          : "border-[#2C2731]/30 bg-[#2C2731]/5 text-[#2C2731]/50 cursor-not-allowed"
                      }`}
                    />
                    <p className="mt-1 text-xs text-[#2C2731]/60">
                      Optional. If left empty, the email body will be used for SMS.
                    </p>
                  </div>

                  {/* Modify Variables and Key/Description - Only for Edit */}
                  {editingTemplate && (
                    <div className="border-t border-[#2C2731]/20 pt-4 mt-2">
                      <div className="flex gap-6 mb-3">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={modifyVariables}
                            onChange={(e) => setModifyVariables(e.target.checked)}
                            className="h-4 w-4 rounded border-[#2C2731] text-[#2C2731] focus:ring-2 focus:ring-[#2C2731]/30"
                          />
                          <span className="text-sm font-medium text-[#2C2731]">Modify Variables</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={modifyKeyAndDescription}
                            onChange={(e) => setModifyKeyAndDescription(e.target.checked)}
                            className="h-4 w-4 rounded border-[#2C2731] text-[#2C2731] focus:ring-2 focus:ring-[#2C2731]/30"
                          />
                          <span className="text-sm font-medium text-[#2C2731]">Modify Key & Description</span>
                        </label>
                      </div>

                      {modifyVariables && (
                        <>
                          <div className="mb-3 rounded-lg border border-[#B24E4E]/40 bg-[#FCEDEA] px-3 py-2 text-xs text-[#B24E4E]">
                            <p className="font-medium mb-1">⚠️ Warning</p>
                            <p>
                              These variables may not be currently passed to the template engine. Contact the maintainer if you'd like more info added.
                            </p>
                          </div>
                          <div>
                            <label className="mb-1 block text-sm font-medium text-[#2C2731]/80">
                              Template Variables
                            </label>
                            <input
                              type="text"
                              value={formData.variables}
                              onChange={(e) => setFormData({ ...formData, variables: e.target.value })}
                              placeholder="e.g., user.name, session.date, guide.name"
                              className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                            />
                            <p className="mt-1 text-xs text-[#2C2731]/60">
                              Comma-separated list of allowed variables
                            </p>
                          </div>
                        </>
                      )}

                      {modifyKeyAndDescription && (
                        <>
                          <div className="mb-3 rounded-lg border border-[#B24E4E]/40 bg-[#FCEDEA] px-3 py-2 text-xs text-[#B24E4E]">
                            <p className="font-medium mb-1">⚠️ Warning</p>
                            <p>
                              If you choose a non-existent key, the notification will not fire. Only modify the key if you have updated the backend code to reference the new key.
                            </p>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <label className="mb-1 block text-sm font-medium text-[#2C2731]/80">
                                Template Key *
                              </label>
                              <input
                                type="text"
                                required={modifyKeyAndDescription}
                                value={formData.key}
                                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                                placeholder="e.g., welcome_email, session_reminder"
                                className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                              />
                              <p className="mt-1 text-xs text-[#2C2731]/60">
                                Unique identifier for code-based lookup
                              </p>
                            </div>

                            <div>
                              <label className="mb-1 block text-sm font-medium text-[#2C2731]/80">
                                Description
                              </label>
                              <input
                                type="text"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Brief description of when this template is used"
                                className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                  {/* Actions */}
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="rounded-lg border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] hover:bg-[#F2EAC3]"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] hover:bg-[#3C3450] disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : editingTemplate ? "Update" : "Create"}
                    </button>
                  </div>
                </form>

                {/* Variables Sidebar */}
                <div className="w-72 border-l border-[#2C2731]/20 bg-[#2C2731]/5 p-6">
                  <h3 className="mb-3 text-sm font-semibold text-[#2C2731]">
                    Available Variables
                  </h3>
                  <p className="mb-4 text-xs text-[#2C2731]/60">
                    Click a variable to insert it at your cursor position in the subject, email body, or SMS body field.
                  </p>

                  {(() => {
                    // For editing: use template's variables unless modifyVariables is enabled
                    // For creating: use form input variables
                    const variables = editingTemplate && !modifyVariables
                      ? editingTemplate.variables
                      : formData.variables
                          .split(",")
                          .map((v) => v.trim())
                          .filter((v) => v.length > 0);

                    return !variables || variables.length === 0 ? (
                      <p className="text-xs italic text-[#2C2731]/50">
                        {editingTemplate && !modifyVariables
                          ? "No variables defined for this template."
                          : "Add variables in the form to see them here."}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {variables.map((variable, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => insertVariable(variable)}
                            className="w-full rounded-lg border border-[#2C2731]/30 bg-white px-3 py-2 text-left text-sm font-mono text-[#2C2731] hover:bg-[#F2EAC3] hover:border-[#2C2731] transition-colors break-words"
                          >
                            {`{{${variable}}}`}
                          </button>
                        ))}
                      </div>
                    );
                  })()}

                  {activeField && (
                    <div className="mt-6 rounded-lg border border-[#214D3C]/30 bg-[#E4F1E8] px-3 py-2">
                      <p className="text-xs text-[#214D3C]">
                        <span className="font-medium">Active field:</span>{" "}
                        {activeField === "subject" ? "Subject" : activeField === "sms_body" ? "SMS Body" : "Email Body"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
